import {
  Button,
  DialogClose,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogRoot,
  DialogTitle,
  Input,
  ScrollArea,
} from '@ibsys/kit';
import { FC, useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { ColorParameterBlock, ThemeValue } from '@/entities/themes';
import { useCreateTheme } from '@/entities/themes';
import { DESTRUCTIVE_BUTTON_CLASS } from '@/shared/lib';
import { Dropzone } from '@/shared/ui/Dropzone';
import { getHexAlpha, getHexWithoutAlpha, hexToHsl } from '../../lib/colorUtils';
import { ColorParameterCard } from '../ColorParameterCard';

type CreateThemeDialogProps = {
  isOpen: boolean;
  onClose: () => void;
  colorBlocks: ColorParameterBlock[];
  /** Theme from the active tab to use as default values when opening */
  defaultTheme?: ThemeValue;
};

type ColorValue = {
  parameterCode: string;
  colorHex: string;
  colorHslH: string;
  colorHslS: string;
  colorHslL: string;
  colorHslA?: string;
};

const fileToDataUrl = (file: File): Promise<string> =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result as string);
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });

export const CreateThemeDialog: FC<CreateThemeDialogProps> = ({ isOpen, onClose, colorBlocks, defaultTheme }) => {
  const { t } = useTranslation(['common', 'themes']);

  const [name, setName] = useState('');
  const [code, setCode] = useState('');
  const [logoSvg, setLogoSvg] = useState('');
  const [favicon, setFavicon] = useState('');
  const [colors, setColors] = useState<Record<string, string>>({});
  const [errors, setErrors] = useState<Record<string, string>>({});

  const defaultHex = '#000000';

  const { mutateAsync: createTheme, isPending: isCreating } = useCreateTheme();

  const allParameterCodes = useMemo(() => {
    const codes: string[] = [];
    for (const block of colorBlocks) {
      for (const param of block.colorParameter) {
        codes.push(param.code);
      }
    }
    return codes;
  }, [colorBlocks]);

  useEffect(() => {
    if (!isOpen) return;

    if (defaultTheme) {
      setName(defaultTheme.name);
      setCode(`${defaultTheme.code}-copy`);
      setLogoSvg(defaultTheme.logoSvg ?? '');
      setFavicon('');
      const initialColors: Record<string, string> = {};
      for (const v of defaultTheme.colorParameterValues) {
        initialColors[v.parameterCode] = v.colorHex;
      }
      for (const c of allParameterCodes) {
        if (!(c in initialColors)) initialColors[c] = defaultHex;
      }
      setColors(initialColors);
    } else {
      setName('');
      setCode('');
      setLogoSvg('');
      setFavicon('');
      if (allParameterCodes.length > 0) {
        setColors((prev) => {
          const next = { ...prev };
          let changed = false;
          for (const c of allParameterCodes) {
            if (!(c in next)) {
              next[c] = defaultHex;
              changed = true;
            }
          }
          return changed ? next : prev;
        });
      }
    }
  }, [isOpen, defaultTheme, allParameterCodes]);

  const validate = useCallback(() => {
    const newErrors: Record<string, string> = {};

    if (!name.trim()) {
      newErrors.name = t('themes:validation.nameRequired');
    }

    if (!code.trim()) {
      newErrors.code = t('themes:validation.codeRequired');
    } else if (!/^[a-zA-Z0-9-]+$/.test(code)) {
      newErrors.code = t('themes:validation.codeFormat');
    }

    const missingColors = allParameterCodes.filter((c) => !colors[c]);
    if (missingColors.length > 0) {
      newErrors.colors = t('themes:validation.allColorsRequired');
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  }, [name, code, colors, allParameterCodes, t]);

  const handleColorChange = useCallback((paramCode: string, hex: string) => {
    setColors((prev) => ({
      ...prev,
      [paramCode]: hex,
    }));
  }, []);

  const handleLogoFiles = useCallback(async (files: File[]) => {
    if (files.length > 0) {
      const dataUrl = await fileToDataUrl(files[0]);
      setLogoSvg(dataUrl);
    }
  }, []);

  const handleFaviconFiles = useCallback(async (files: File[]) => {
    if (files.length > 0) {
      const dataUrl = await fileToDataUrl(files[0]);
      setFavicon(dataUrl);
    }
  }, []);

  const handleCreate = useCallback(async () => {
    if (!validate()) return;

    const colorParameterValues: ColorValue[] = allParameterCodes.map((paramCode) => {
      const hex = colors[paramCode] || defaultHex;
      const hex6 = getHexWithoutAlpha(hex);
      const alpha = getHexAlpha(hex);
      const hsl = hexToHsl(hex6);
      return {
        parameterCode: paramCode,
        colorHex: hex,
        colorHslH: hsl.h.toString(),
        colorHslS: hsl.s.toString(),
        colorHslL: hsl.l.toString(),
        colorHslA: String(alpha),
      };
    });

    await createTheme({
      name: name.trim(),
      code: code.trim(),
      logoSvg: logoSvg.trim(),
      favicon: favicon.trim() || undefined,
      colorParameterValues,
    });

    setName('');
    setCode('');
    setLogoSvg('');
    setFavicon('');
    setColors({});
    setErrors({});
    onClose();
  }, [validate, allParameterCodes, colors, name, code, logoSvg, favicon, createTheme, onClose]);

  const handleOpenChange = useCallback(
    (open: boolean) => {
      if (!open) {
        setName('');
        setCode('');
        setLogoSvg('');
        setFavicon('');
        setColors({});
        setErrors({});
        onClose();
      }
    },
    [onClose],
  );

  return (
    <DialogRoot open={isOpen} onOpenChange={handleOpenChange}>
      <DialogContent className="max-w-2xl max-h-[80vh] p-2 overflow-hidden flex flex-col">
        <DialogHeader>
          <DialogTitle>{t('themes:createDialog.title')}</DialogTitle>
        </DialogHeader>

        <ScrollArea className="h-full flex flex-col">
          <div className="flex flex-col gap-4 py-4 px-2 h-full">
            <div className="grid grid-cols-2 gap-4">
              <div className="flex flex-col gap-1">
                <Input
                  label={t('themes:themeName')}
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  placeholder={t('themes:createDialog.namePlaceholder')}
                />
                {errors.name && <span className="text-xs text-destructive">{errors.name}</span>}
              </div>

              <div className="flex flex-col gap-1">
                <Input
                  label={t('themes:themeCode')}
                  value={code}
                  onChange={(e) => setCode(e.target.value)}
                  placeholder={t('themes:createDialog.codePlaceholder')}
                />
                {errors.code && <span className="text-xs text-destructive">{errors.code}</span>}
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="flex flex-col gap-2">
                <span className="text-sm font-medium">{t('themes:createDialog.logoLabel')}</span>
                {logoSvg ? (
                  <div className="flex items-center gap-2">
                    <img src={logoSvg} alt="logo" className="h-10 max-w-32 object-contain" />
                    <Button
                      variant="ghost"
                      color="destructive"
                      className={DESTRUCTIVE_BUTTON_CLASS}
                      size="sm"
                      onClick={() => setLogoSvg('')}
                    >
                      {t('common:delete')}
                    </Button>
                  </div>
                ) : (
                  <Dropzone
                    acceptedFileTypes={['.svg', '.png', '.jpg', '.jpeg', '.webp']}
                    maxFiles={1}
                    maxFileSize={5}
                    onFilesChange={handleLogoFiles}
                  />
                )}
              </div>

              <div className="flex flex-col gap-2">
                <span className="text-sm font-medium">{t('themes:createDialog.faviconLabel')}</span>
                {favicon ? (
                  <div className="flex items-center gap-2">
                    <img src={favicon} alt="favicon" className="size-8 object-contain" />
                    <Button
                      variant="ghost"
                      color="destructive"
                      className={DESTRUCTIVE_BUTTON_CLASS}
                      size="sm"
                      onClick={() => setFavicon('')}
                    >
                      {t('common:delete')}
                    </Button>
                  </div>
                ) : (
                  <Dropzone
                    acceptedFileTypes={['.ico', '.png', '.svg']}
                    maxFiles={1}
                    maxFileSize={1}
                    onFilesChange={handleFaviconFiles}
                  />
                )}
              </div>
            </div>

            {errors.colors && <span className="text-sm text-destructive">{errors.colors}</span>}

            {colorBlocks.map((block) => (
              <div key={block.code} className="flex flex-col gap-2">
                <h4 className="text-sm font-semibold text-foreground">{block.name}</h4>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                  {block.colorParameter.map((param) => {
                    const colorHex = colors[param.code] || defaultHex;
                    const hex6 = getHexWithoutAlpha(colorHex);
                    const alpha = getHexAlpha(colorHex);
                    const hsl = hexToHsl(hex6);
                    const item = {
                      ...param,
                      colorHex,
                      colorHslH: String(hsl.h),
                      colorHslS: String(hsl.s),
                      colorHslL: String(hsl.l),
                      colorHslA: String(alpha),
                    };
                    return (
                      <ColorParameterCard
                        key={param.code}
                        item={item}
                        onColorChange={(hex) => handleColorChange(param.code, hex)}
                      />
                    );
                  })}
                </div>
              </div>
            ))}
          </div>
        </ScrollArea>

        <DialogFooter className="flex gap-2">
          <DialogClose>
            <Button variant="ghost">{t('cancel')}</Button>
          </DialogClose>
          <Button onClick={handleCreate} isLoading={isCreating}>
            {t('themes:createTheme')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </DialogRoot>
  );
};
